import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import accountingApi from '../services/accountingApi';

interface DashboardData {
    total_debits: number;
    total_credits: number;
    total_vouchers: number;
    draft_vouchers_count: number;
    recent_vouchers: any[];
}

function AccountingDashboard() {
    const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        loadDashboardData();
    }, []);

    const loadDashboardData = async () => {
        try {
            setLoading(true);
            console.log('AccountingDashboard: Fetching dashboard data...');
            const response = await accountingApi.getDashboard();
            console.log('AccountingDashboard: API Response', response);
            if (response.success) {
                setDashboardData(response.data);
            } else {
                setError(response.message || 'Failed to load dashboard data');
            }
        } catch (err: any) {
            console.error('Error fetching dashboard data:', err);
            setError('An unexpected error occurred while loading dashboard');
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div className="p-8 text-center">Loading dashboard...</div>;
    if (error) return <div className="p-8 text-center text-rose-600">{error}</div>;

    const stats = [
        {
            label: 'Total Debits', value: dashboardData?.total_debits || 0, color: 'text-indigo-600', icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" /></svg>
            )
        },
        {
            label: 'Total Credits', value: dashboardData?.total_credits || 0, color: 'text-rose-600', icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 17h8m0 0v-8m0 8l-8-8-4 4-6-6" /></svg>
            )
        },
        {
            label: 'Total Vouchers', value: dashboardData?.total_vouchers || 0, color: 'text-blue-600', icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
            )
        },
        {
            label: 'Draft Vouchers', value: dashboardData?.draft_vouchers_count || 0, color: 'text-amber-600', icon: (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
            )
        },
    ];

    return (
        <div className="max-w-7xl mx-auto px-4 py-8 space-y-8">
            <div className="flex justify-between items-center">
                <h1 className="text-3xl font-bold text-slate-800">Accounting Dashboard</h1>
                <div className="flex gap-3">
                    <Link to="/accounting/vouchers/new" className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors">
                        + New Voucher
                    </Link>
                </div>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                {stats.map((stat, idx) => (
                    <div key={idx} className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
                        <div className="flex items-center justify-between mb-4">
                            <div className={`p-2 rounded-lg bg-slate-50 ${stat.color}`}>
                                {stat.icon}
                            </div>
                        </div>
                        <h3 className="text-slate-500 text-sm font-medium">{stat.label}</h3>
                        <p className={`text-2xl font-bold mt-1 ${stat.color}`}>
                            {typeof stat.value === 'number' && stat.label.includes('Total') && !stat.label.includes('Vouchers')
                                ? `₹${stat.value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`
                                : stat.value}
                        </p>
                    </div>
                ))}
            </div>

            {/* Financial Timeline & Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Financial Timeline */}
                <div className="lg:col-span-2">
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                        <div className="p-6 border-b border-slate-100 flex items-center justify-between">
                            <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                                <svg className="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Financial Timeline
                            </h2>
                            <select className="text-sm border-slate-200 rounded-lg text-slate-600 focus:ring-indigo-500 font-medium bg-slate-50 border px-3 py-1.5">
                                <option>This Month</option>
                                <option>Last Month</option>
                                <option>This Quarter</option>
                            </select>
                        </div>
                        <div className="p-6">
                            <div className="relative pl-8 border-l-2 border-indigo-100 space-y-8">
                                {/* Timeline Items */}
                                {dashboardData?.recent_vouchers?.slice(0, 5).map((voucher, idx) => (
                                    <div key={voucher.id} className="relative group">
                                        {/* Dot */}
                                        <div className={`absolute -left-[41px] w-5 h-5 rounded-full border-4 border-white shadow-sm flex items-center justify-center ${voucher.voucher_type === 'Payment' ? 'bg-rose-500' :
                                                voucher.voucher_type === 'Receipt' ? 'bg-emerald-500' : 'bg-indigo-500'
                                            }`}></div>

                                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-4 bg-slate-50/50 rounded-xl hover:bg-white hover:shadow-md transition-all border border-slate-100/50 hover:border-indigo-100">
                                            <div>
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-md ${voucher.voucher_type === 'Payment' ? 'bg-rose-100 text-rose-700' :
                                                            voucher.voucher_type === 'Receipt' ? 'bg-emerald-100 text-emerald-700' : 'bg-indigo-100 text-indigo-700'
                                                        }`}>{voucher.voucher_type || 'Journal'}</span>
                                                    <span className="text-xs font-bold text-slate-400">{voucher.voucher_number}</span>
                                                </div>
                                                <p className="text-sm font-bold text-slate-800">{voucher.narration || 'No description provided'}</p>
                                                <p className="text-xs text-slate-500 font-medium mt-0.5">{voucher.voucher_date}</p>
                                            </div>
                                            <div className="text-right">
                                                <p className={`text-lg font-black ${voucher.voucher_type === 'Payment' ? 'text-rose-600' :
                                                        voucher.voucher_type === 'Receipt' ? 'text-emerald-600' : 'text-slate-800'
                                                    }`}>
                                                    {voucher.voucher_type === 'Payment' ? '-' : '+'}₹{voucher.total_debit.toLocaleString()}
                                                </p>
                                                <span className={`text-[10px] uppercase font-bold tracking-wider ${voucher.status === 'Posted' ? 'text-emerald-500' : 'text-amber-500'
                                                    }`}>{voucher.status}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}

                                {/* Empty State if no vouchers */}
                                {(!dashboardData?.recent_vouchers || dashboardData.recent_vouchers.length === 0) && (
                                    <div className="text-center py-8">
                                        <p className="text-slate-400 italic">No financial activity recorded in this period.</p>
                                    </div>
                                )}
                            </div>
                            <div className="mt-8 text-center">
                                <Link to="/accounting/vouchers" className="text-sm font-bold text-indigo-600 hover:text-indigo-800 hover:underline">
                                    View Full Financial History →
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Column: Key Metrics & Quick Access */}
                <div className="space-y-6">
                    {/* Cash Flow Card */}
                    <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl shadow-lg p-6 text-white relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-16 -mt-16"></div>
                        <h3 className="text-xs font-black text-slate-300 uppercase tracking-widest mb-4">Net Cash Flow</h3>

                        <div className="space-y-4 relative z-10">
                            <div>
                                <p className="text-xs text-slate-400 mb-1">Inflow</p>
                                <p className="text-xl font-bold text-emerald-400">+₹{(dashboardData?.total_credits || 0).toLocaleString()}</p>
                            </div>
                            <div>
                                <p className="text-xs text-slate-400 mb-1">Outflow</p>
                                <p className="text-xl font-bold text-rose-400">-₹{(dashboardData?.total_debits || 0).toLocaleString()}</p>
                            </div>
                            <div className="pt-4 border-t border-white/10 mt-2">
                                <p className="text-xs text-slate-400 mb-1">Net Position</p>
                                <p className={`text-3xl font-black ${(dashboardData?.total_credits || 0) - (dashboardData?.total_debits || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                                    ₹{((dashboardData?.total_credits || 0) - (dashboardData?.total_debits || 0)).toLocaleString()}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Quick Transfer */}
                    <div className="bg-indigo-50 rounded-xl border border-indigo-100 p-6">
                        <h3 className="text-sm font-bold text-indigo-900 mb-4">Quick Shortcuts</h3>
                        <div className="space-y-2">
                            <Link to="/accounting/vouchers/new?type=Payment" className="block w-full py-2 px-4 bg-white border border-indigo-100 rounded-lg text-sm font-medium text-indigo-600 hover:bg-indigo-600 hover:text-white transition-colors text-center shadow-sm">
                                Record Payment
                            </Link>
                            <Link to="/accounting/vouchers/new?type=Receipt" className="block w-full py-2 px-4 bg-white border border-indigo-100 rounded-lg text-sm font-medium text-indigo-600 hover:bg-indigo-600 hover:text-white transition-colors text-center shadow-sm">
                                Record Receipt
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modules Quick Access */}
            <div className="space-y-4">
                <h2 className="text-xl font-bold text-slate-800">Quick Access</h2>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Link to="/accounting/account-groups" className="p-4 bg-indigo-50 border border-indigo-100 rounded-xl hover:bg-indigo-100 transition-colors group">
                        <span className="block font-bold text-indigo-700 group-hover:translate-x-1 transition-transform">Account Groups →</span>
                    </Link>
                    <Link to="/accounting/account-heads" className="p-4 bg-emerald-50 border border-emerald-100 rounded-xl hover:bg-emerald-100 transition-colors group">
                        <span className="block font-bold text-emerald-700 group-hover:translate-x-1 transition-transform">Account Heads →</span>
                    </Link>
                    <Link to="/accounting/bank-accounts" className="p-4 bg-blue-50 border border-blue-100 rounded-xl hover:bg-blue-100 transition-colors group">
                        <span className="block font-bold text-blue-700 group-hover:translate-x-1 transition-transform">Bank Accounts →</span>
                    </Link>
                    <Link to="/accounting/vouchers" className="p-4 bg-amber-50 border border-amber-100 rounded-xl hover:bg-amber-100 transition-colors group">
                        <span className="block font-bold text-amber-700 group-hover:translate-x-1 transition-transform">Vouchers List →</span>
                    </Link>
                </div>
            </div>
        </div>
    );
}

export default AccountingDashboard;
